import {
  ReactNode,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import useMediaQuery from "@mui/material/useMediaQuery";
import { buildClasses } from "../utils/buildClasses";
import { RYE_PANEL_SPLIT_GAP, RyePanelSplitContext } from "./RyeScreenWrapper";
import { RYE_SLIDE_EASING, RYE_SLIDE_MS } from "./motion";

const KEYBOARD_STEP = 24;

/** Keeps sizing independent of the pane content and its scroll position. */
export function RyeMainContentSplitPane({
  children,
  bottomPane,
  bottomPaneLabel,
  minTopHeight = 220,
  minBottomHeight = 180,
}: {
  children: ReactNode;
  /** Pass null to hide the lower pane while preserving the chosen split. */
  bottomPane: ReactNode | null;
  bottomPaneLabel: string;
  minTopHeight?: number;
  minBottomHeight?: number;
}): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{
    pointerId: number;
    startY: number;
    height: number;
  } | null>(null);
  const paneId = useId();
  const [containerHeight, setContainerHeight] = useState(0);
  const [fraction, setFraction] = useState(0.5);
  const [dragging, setDragging] = useState(false);
  const isOpen = bottomPane !== null;
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const [retainedPane, setRetainedPane] = useState(bottomPane);

  // Keep the last selection visible until it has slid out of the container.
  // Reopening cancels cleanup, including when a different selection is opened.
  useEffect(() => {
    if (isOpen) {
      setRetainedPane(bottomPane);
      return;
    }
    if (reduceMotion) {
      setRetainedPane(null);
      return;
    }
    const timeout = window.setTimeout(
      () => setRetainedPane(null),
      RYE_SLIDE_MS + 50,
    );
    return () => window.clearTimeout(timeout);
  }, [bottomPane, isOpen, reduceMotion]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const measure = () =>
      setContainerHeight(container.getBoundingClientRect().height);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!dragging || !isOpen) return;
    const { cursor, userSelect } = document.body.style;
    document.body.style.cursor = "row-resize";
    document.body.style.userSelect = "none";
    return () => {
      document.body.style.cursor = cursor;
      document.body.style.userSelect = userSelect;
    };
  }, [dragging, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      dragRef.current = null;
      setDragging(false);
    }
  }, [isOpen]);

  const availableHeight = Math.max(0, containerHeight - RYE_PANEL_SPLIT_GAP);
  // On short windows, share the available space instead of overflowing the layout.
  const minimumScale = Math.min(
    1,
    availableHeight / Math.max(1, minTopHeight + minBottomHeight),
  );
  const minHeight = minBottomHeight * minimumScale;
  const maxHeight = availableHeight - minTopHeight * minimumScale;
  const paneHeight = Math.min(
    maxHeight,
    Math.max(minHeight, availableHeight * fraction),
  );
  // The screen's panel draws the lower section as a panel of its own, so it
  // follows the section's height and slide.
  const setPanelSplit = useContext(RyePanelSplitContext);
  const sectionHeight = isOpen ? paneHeight + RYE_PANEL_SPLIT_GAP : 0;
  const slideDurationMs = dragging || reduceMotion ? 0 : RYE_SLIDE_MS;
  const showBottomPanel = (bottomPane ?? retainedPane) !== null;
  useLayoutEffect(() => {
    setPanelSplit?.({
      inset: sectionHeight,
      durationMs: slideDurationMs,
      showBottom: showBottomPanel,
    });
  }, [setPanelSplit, sectionHeight, slideDurationMs, showBottomPanel]);
  useLayoutEffect(
    () => () => setPanelSplit?.({ inset: 0, durationMs: 0, showBottom: false }),
    [setPanelSplit],
  );

  function resize(height: number) {
    if (availableHeight > 0) {
      setFraction(
        Math.min(maxHeight, Math.max(minHeight, height)) / availableHeight,
      );
    }
  }

  return (
    <div
      ref={containerRef}
      className={buildClasses(
        "flex",
        "flex-col",
        "flex-grow",
        "min-h-0",
        "h-1",
        "overflow-hidden",
      )}
    >
      <div className={buildClasses("min-h-0", "flex-1")}>{children}</div>
      <div
        aria-hidden={!isOpen}
        {...{ inert: !isOpen ? "" : undefined }}
        onTransitionEnd={(event) => {
          if (
            event.target === event.currentTarget &&
            event.propertyName === "height" &&
            !isOpen
          ) {
            setRetainedPane(null);
          }
        }}
        className={buildClasses(
          "shrink-0",
          !isOpen ? "pointer-events-none" : "",
        )}
        style={{
          height: sectionHeight,
          transitionProperty: "height",
          transitionDuration: `${slideDurationMs}ms`,
          transitionTimingFunction: RYE_SLIDE_EASING,
        }}
      >
        {(bottomPane ?? retainedPane) !== null && (
          <>
            {/* The strip of page between the panels is the grab area. */}
            <div
              className={buildClasses("relative", "z-30", "shrink-0")}
              style={{ height: RYE_PANEL_SPLIT_GAP }}
            >
              <div
                role="separator"
                aria-label={`Resize ${bottomPaneLabel}`}
                aria-orientation="horizontal"
                aria-controls={paneId}
                aria-valuemin={Math.round(minHeight)}
                aria-valuemax={Math.round(maxHeight)}
                aria-valuenow={Math.round(paneHeight)}
                aria-valuetext={`${Math.round(paneHeight)} pixels tall`}
                tabIndex={0}
                className={buildClasses(
                  "absolute",
                  "inset-0",
                  "cursor-row-resize",
                  "touch-none",
                  "select-none",
                  "focus-visible:outline-none",
                  "focus-visible:ring-2",
                  "focus-visible:ring-inset",
                  "focus-visible:ring-purple",
                )}
                onPointerDown={(event) => {
                  if (event.button !== 0 || availableHeight === 0) return;
                  event.preventDefault();
                  event.currentTarget.focus();
                  event.currentTarget.setPointerCapture(event.pointerId);
                  dragRef.current = {
                    pointerId: event.pointerId,
                    startY: event.clientY,
                    height: paneHeight,
                  };
                  setDragging(true);
                }}
                onPointerMove={(event) => {
                  const drag = dragRef.current;
                  if (drag && drag.pointerId === event.pointerId) {
                    resize(drag.height + drag.startY - event.clientY);
                  }
                }}
                onPointerUp={(event) => {
                  if (
                    !dragRef.current ||
                    dragRef.current.pointerId !== event.pointerId
                  )
                    return;
                  dragRef.current = null;
                  setDragging(false);
                  event.currentTarget.releasePointerCapture(event.pointerId);
                }}
                onPointerCancel={() => {
                  dragRef.current = null;
                  setDragging(false);
                }}
                onLostPointerCapture={() => {
                  dragRef.current = null;
                  setDragging(false);
                }}
                onKeyDown={(event) => {
                  const heights: Record<string, number> = {
                    ArrowUp: paneHeight + KEYBOARD_STEP,
                    ArrowDown: paneHeight - KEYBOARD_STEP,
                    Home: minHeight,
                    End: maxHeight,
                  };
                  if (event.key in heights) {
                    event.preventDefault();
                    resize(heights[event.key]);
                  }
                }}
              />
            </div>
            <div
              id={paneId}
              className={buildClasses("min-h-0", "shrink-0")}
              style={{ height: paneHeight }}
            >
              {bottomPane ?? retainedPane}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
